import { z } from 'zod';
import {
  conditionBandSchema,
  marketabilityTierSchema,
} from './asset.js';

/**
 * FMV snapshot detail DTO — the audit-lock spine.
 *
 * Mirrors db/schema/301_views_snapshot.sql (v_fmv_snapshot_inputs).
 * Verbatim captured inputs (the snapshot row itself) are the source of
 * truth. The advisory rows (ageCurveRow, conditionMultiplierRow,
 * frictionRuleRows, oemFloorRow, itadRateRow) are best-effort
 * reconstructions of the config that was active at priced_at; surface
 * them with an explicit disclaimer in the UI.
 *
 * fmv_v1_recomputed is the raw multiplier product
 *   baseline × age × config × condition × marketability
 * and is NOT necessarily equal to fmv_med_usd: the engine applies a
 * range-width adjustment (see PRD §8.3.3 + comments in 100_fmv_pricing.sql).
 * Display the two side-by-side; the analyst decides what equality means.
 */

export const frictionBreakdownSchema = z
  .record(z.coerce.number())
  .nullable();

export const confidenceBreakdownSchema = z.record(z.unknown()).nullable();

export const snapshotDetailSchema = z.object({
  snapshotId: z.string().uuid(),
  assetId: z.string().uuid(),
  sellerOrgId: z.string().uuid(),
  sellerLegalName: z.string(),
  pricedAt: z.string(),
  modelVersionId: z.string().uuid(),
  modelVersion: z.string(),
  modelFamily: z.string(),
  triggerId: z.string(),
  triggerName: z.string(),
  triggeredByUserId: z.string().uuid().nullable(),
  triggeredByEmail: z.string().nullable(),

  // ===== Outputs =====
  fmvLowUsd: z.coerce.number(),
  fmvMedUsd: z.coerce.number(),
  fmvHighUsd: z.coerce.number(),
  netRecoveryUsd: z.coerce.number(),
  itadBaselineUsd: z.coerce.number().nullable(),
  deltaVsItadUsd: z.coerce.number().nullable(),
  marketabilityTier: marketabilityTierSchema,
  confidenceScore: z.coerce.number(),
  confidenceBreakdown: confidenceBreakdownSchema,

  // ===== Verbatim inputs (PRD Audit-Lock™) =====
  baselinePriceUsd: z.coerce.number(),
  baselinePriceSourceId: z.string(),
  baselinePriceSourceName: z.string(),
  baselinePriceSampleSize: z.number().nullable(),
  ageMultiplier: z.coerce.number(),
  configMultiplier: z.coerce.number(),
  conditionMultiplierValue: z.coerce.number(),
  marketabilityFactor: z.coerce.number(),
  frictionBreakdown: frictionBreakdownSchema,
  frictionTotalUsd: z.coerce.number(),
  oemFloorUsd: z.coerce.number().nullable(),

  // ===== Context at calc time =====
  assetClassId: z.string(),
  assetClassName: z.string(),
  conditionBand: conditionBandSchema,
  ageMonthsAtCalc: z.number().nullable(),
  telemetryWindowStart: z.string().nullable(),
  telemetryWindowEnd: z.string().nullable(),
  notes: z.string().nullable(),

  // ===== Advisory as-of config (jsonb) =====
  ageCurveRow: z.record(z.unknown()).nullable(),
  conditionMultiplierRow: z.record(z.unknown()).nullable(),
  frictionRuleRows: z.array(z.record(z.unknown())).nullable(),
  oemFloorRow: z.record(z.unknown()).nullable(),
  itadRateRow: z.record(z.unknown()).nullable(),

  // ===== Reproducibility =====
  fmvV1Recomputed: z.coerce.number(),

  // ===== Baseline provenance (SIL-1831, db/schema/1067) =====
  // How baselinePriceUsd (the EFFECTIVE value) was derived from its sku_mcmp
  // row. Lenient jsonb on the READ side (shape owned by
  // fmv-baseline-provenance.ts) so a future envelope version cannot 500 the
  // audit view. Null = baseline used as read, or a pre-1067 row.
  baselineProvenance: z.record(z.unknown()).nullish().transform((v) => v ?? null),

  // ===== Value-basis disclosure (SIL-924) =====
  // Same LIVE-signal caveat as assetSnapshotHistoryItemSchema (packages/
  // shared/src/dto/asset.ts): computed at read time from the asset's
  // current SKU's freshest qualifying market_comp row, not from a
  // persisted point-in-time value. Only meaningful when
  // baselinePriceSourceId === 'sku_mcmp'.
  compNewestObservedAt: z.string().nullable(),
  compRecencyDays: z.coerce.number().nullable(),
  staleDataException: z.boolean(),

  // ===== Directional label (SIL-1828, R2) =====
  // Server-derived from baselinePriceSourceId = 'virtual_comp'; UIs never re-derive.
  isDirectionalBaseline: z.boolean().default(false),
});

export type SnapshotDetail= z.infer<typeof snapshotDetailSchema>;

export const snapshotDetailResponseSchema = z.object({
  detail: snapshotDetailSchema,
});
export type SnapshotDetailResponse = z.infer<typeof snapshotDetailResponseSchema>;
